import React from 'react';
import { WifiOff, Wifi, AlertCircle, RefreshCw } from 'lucide-react';
import { useOnlineStatus } from '../../hooks/useStability';
import { useLanguage } from '../../contexts/LanguageContext';

/**
 * NetworkStatusIndicator - Shows network connectivity status
 * Displays a banner when offline and optionally shows reconnection status
 */
const NetworkStatusIndicator = ({ 
  showWhenOnline = false,
  position = 'top', // 'top' | 'bottom'
  className = ''
}) => {
  const { t } = useLanguage();
  const isOnline = useOnlineStatus();
  const [wasOffline, setWasOffline] = React.useState(false);
  const [showReconnected, setShowReconnected] = React.useState(false);

  React.useEffect(() => {
    if (!isOnline) {
      setWasOffline(true);
    } else if (wasOffline) {
      setShowReconnected(true);
      const timer = setTimeout(() => {
        setShowReconnected(false);
        setWasOffline(false);
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [isOnline, wasOffline]);

  // Don't render anything if online and not showing online status
  if (isOnline && !showWhenOnline && !showReconnected) {
    return null;
  }

  const positionClasses = position === 'top' 
    ? 'top-0 left-0 right-0' 
    : 'bottom-0 left-0 right-0';

  const positionStyle = position === 'bottom' 
    ? { paddingBottom: 'max(env(safe-area-inset-bottom, 0px), 8px)' } 
    : undefined;

  if (!isOnline) {
    return (
      <div 
        className={`fixed ${positionClasses} z-50 bg-amber-500 text-white px-4 py-2 flex items-center justify-center gap-2 shadow-lg animate-slide-down ${className}`}
        role="alert"
        aria-live="polite"
        style={positionStyle}
      >
        <WifiOff className="w-4 h-4" />
        <span className="text-sm font-medium">{t('youAreCurrentlyOffline')}</span>
        <span className="text-xs opacity-80 hidden sm:inline">• {t('changesWillBeSaved')}</span>
      </div>
    );
  }

  if (showReconnected) {
    return (
      <div 
        className={`fixed ${positionClasses} z-50 bg-emerald-500 text-white px-4 py-2 flex items-center justify-center gap-2 shadow-lg animate-slide-down ${className}`}
        role="alert"
        aria-live="polite"
        style={positionStyle}
      >
        <Wifi className="w-4 h-4" />
        <span className="text-sm font-medium">{t('backOnline')}</span>
      </div>
    );
  }

  if (showWhenOnline && isOnline) {
    return (
      <div 
        className={`fixed ${positionClasses} z-50 bg-emerald-500/90 text-white px-4 py-2 flex items-center justify-center gap-2 ${className}`}
        role="status"
        style={positionStyle}
      >
        <Wifi className="w-4 h-4" />
        <span className="text-sm">{t('connected')}</span>
      </div>
    );
  }

  return null;
};

/**
 * OfflineMessage - Inline message shown when offline
 */
export const OfflineMessage = ({ 
  message,
  className = '' 
}) => {
  const { t } = useLanguage();
  const isOnline = useOnlineStatus();

  if (isOnline) return null;

  return (
    <div 
      className={`flex items-center gap-2 p-3 bg-amber-100 dark:bg-amber-900/30 border border-amber-300 dark:border-amber-700 rounded-lg text-amber-800 dark:text-amber-200 ${className}`}
      role="alert"
    >
      <AlertCircle className="w-4 h-4 flex-shrink-0" />
      <span className="text-sm">{message || t('youAreCurrentlyOffline')}</span>
    </div>
  );
};

/**
 * RetryButton - Button to retry failed operations
 */
export const RetryButton = ({ 
  onRetry, 
  loading = false,
  label,
  className = '' 
}) => {
  const { t } = useLanguage();
  const buttonLabel = label || t('retry') || 'Retry';
  
  return (
    <button
      onClick={onRetry}
      disabled={loading}
      className={`flex items-center gap-2 px-4 py-2 bg-[var(--md3-primary)] text-white rounded-lg hover:opacity-90 disabled:opacity-50 transition-all ${className}`}
    >
      <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
      <span className="text-sm font-medium">{loading ? t('retrying') || 'Retrying...' : buttonLabel}</span>
    </button>
  );
};

/**
 * ConnectionStatus - Compact connection status indicator
 */
export const ConnectionStatus = ({ className = '' }) => {
  const { t } = useLanguage();
  const isOnline = useOnlineStatus();

  return (
    <div 
      className={`flex items-center gap-1.5 ${className}`}
      title={isOnline ? t('connected') : t('offline') || 'Offline'}
    >
      <div 
        className={`w-2 h-2 rounded-full ${
          isOnline ? 'bg-emerald-500' : 'bg-amber-500'
        }`}
      />
      <span className="text-xs text-[var(--md3-outline)]">
        {isOnline ? t('online') || 'Online' : t('offline') || 'Offline'}
      </span>
    </div>
  );
};

export default NetworkStatusIndicator;
